/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import { CssBaseline, CssVarsProvider, GlobalStyles } from "@mui/joy";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "notistack";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "./components";
import { StoreProvider } from "./core/store";
import { theme } from "./core/theme";
import { Router } from "./routes/index";

// Initialize i18n
import "./core/i18n";

// Create a client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <StrictMode>
    <HelmetProvider>
      <CssVarsProvider theme={theme} defaultMode="dark">
        <SnackbarProvider>
          <CssBaseline />
          <GlobalStyles
            styles={{
              ".typer-message": {
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                fontFamily: "var(--joy-fontFamily-body)",
              },
              "::-webkit-scrollbar": {
                width: "4px",
                height: "4px",
                margin: "12px",
              },
              "::-webkit-scrollbar-track": {
                background: "transparent",
                borderRadius: "3px",
                margin: "12px",
              },
              "::-webkit-scrollbar-thumb": {
                background: "var(--joy-palette-neutral-200, rgba(0, 0, 0, 0.2))",
                borderRadius: "4px",
                transition: "background 0.2s ease",
                margin: "2px",
              },
              "::-webkit-scrollbar-thumb:hover": {
                background: "var(--joy-palette-neutral-300, rgba(0, 0, 0, 0.3))",
              },
              '[data-joy-color-scheme="dark"] ::-webkit-scrollbar-track': {
                background: "transparent",
              },
              '[data-joy-color-scheme="dark"] ::-webkit-scrollbar-thumb': {
                background:
                  "var(--joy-palette-neutral-700, rgba(255, 255, 255, 0.1))",
              },
              '[data-joy-color-scheme="dark"] ::-webkit-scrollbar-thumb:hover': {
                background:
                  "var(--joy-palette-neutral-600, rgba(255, 255, 255, 0.2))",
              },
            }}
          />
          <StoreProvider>
            <QueryClientProvider client={queryClient}>
              <LanguageProvider>
                <Router />
              </LanguageProvider>
            </QueryClientProvider>
          </StoreProvider>
        </SnackbarProvider>
      </CssVarsProvider>
    </HelmetProvider>
  </StrictMode>,
);

if (import.meta.hot) {
  import.meta.hot.dispose(() => root.unmount());
}
